// Internet Explorer 11 requires polyfills and partially supported by this project.
// import 'react-app-polyfill/ie11';
// import 'react-app-polyfill/stable';
// First, before Sentry starts recording the URL: strips the L'addition session token from it.
import 'app/pos/api/posWebviewSession';
import React from 'react';
import ReactDOM from 'react-dom';
import 'typeface-muli';
import './i18n';
import './react-chartjs-2-defaults';
import './styles/index.css';
import History from '@history';
import * as Sentry from '@sentry/react';
import App from 'app/App';
import AppErrorBoundary from 'app/components/AppErrorBoundary/AppErrorBoundary';
import { API_URL } from 'app/constants/api.constant';
import { SentryTags } from 'app/constants/app.constant';
import { scrubBreadcrumb, scrubEvent } from 'app/pos/api/webviewSentryScrub';
import {
  extractAssetUrl,
  failedBuildAssetUrl,
  isStaleBuildError,
  recoverFromStaleBuild,
} from 'app/utils/staleBuild.utils';
import axios from 'axios';

// Must stay unique per deploy: a reused release name overwrites its own source maps.
const SENTRY_RELEASE = `lb-saas@${process.env.REACT_APP_RELEASE || '1.0.2'}`;

if (process.env.REACT_APP_ENV && ['staging', 'production'].includes(process.env.REACT_APP_ENV)) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    debug: false,
    enableLogs: true,
    release: SENTRY_RELEASE,
    environment: process.env.REACT_APP_ENV,
    integrations: [Sentry.replayIntegration()],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSend: scrubEvent,
  });
}

// Sentry stamps the URL at capture time, not at throw time, so a crash from a timer left over by
// an unmounted page is filed under whatever route the user navigated to. Tag the real one.
const tagRoute = (pathname: string): void => {
  Sentry.getCurrentScope().setTag(SentryTags.pages, pathname);
};

tagRoute(window.location.pathname);
History.listen(location => tagRoute(location.pathname));

// A dynamic import rejecting outside of render never reaches an error boundary.
window.addEventListener('unhandledrejection', event => {
  Sentry.captureException(event.reason, { tags: { source: 'unhandledrejection' } });

  if (isStaleBuildError(event.reason)) {
    recoverFromStaleBuild(extractAssetUrl(event.reason));
  }
});

// A <script>/<link> that fails to load fires a non-bubbling error event on the element, so
// only a capture-phase listener on window sees it. Narrow to our own build assets: reloading
// on any failed resource (a broken image, a blocked third-party script) would loop forever.
window.addEventListener(
  'error',
  event => {
    const assetUrl = failedBuildAssetUrl(event.target);

    if (!assetUrl) {
      return;
    }

    Sentry.captureMessage(`Build asset failed to load: ${assetUrl}`, {
      tags: { source: 'asset-load-error', stale_build: 'true' },
    });
    recoverFromStaleBuild(assetUrl);
  },
  true,
);

axios.defaults.baseURL = API_URL;
// axios.defaults.headers.common['Authorization'] = 'AUTH TOKEN';
axios.defaults.headers.post['Content-Type'] = 'application/json';

axios.interceptors.request.use(
  request => {
    // Edit request config
    return {
      ...request,
      params: {
        ...request.params,
        access_token: request.headers?.common?.Authorization,
      },
    };
  },
  error => {
    return Promise.reject(error);
  },
);

// Not on the till: a stale cached bundle inside a webview the cashier cannot hard-refresh
// is a service-long outage with no way out from behind the counter. Keyed on the path, not
// the hostname, so it holds on both hosts the till is reachable from.
if ('serviceWorker' in navigator && !window.location.pathname.startsWith('/pos/')) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      // updateViaCache: without it a cached sw.js hides new workers after a deploy.
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .then(reg => {
        console.log('Service Worker registered:', reg);
      })
      .catch(err => {
        console.error('Service Worker registration failed:', err);
      });
  });
} else {
  console.log('Service Worker is not supported in this browser.');
}

axios.interceptors.response.use(
  response => {
    // Edit response config
    return response;
  },
  error => {
    return Promise.reject(error);
  },
);

ReactDOM.render(
  <AppErrorBoundary>
    <App />
  </AppErrorBoundary>,
  document.getElementById('root'),
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
// serviceWorker.unregister();
